import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, LinearProgress, Paper, TextField, Typography,
} from '@mui/material';
import { UploadFile as UploadIcon } from '@mui/icons-material';
import { api, endpoints } from '../config/api';
import { AudioFile, Tour, TourStop } from '../types';

interface Props {
  tour: Tour;
  onClose: () => void;
  onCreated: (tour: Tour) => void;
}

const audioLabel = (audio: AudioFile) => audio.title
  ? `${audio.title} — ${audio.originalName || audio.filename}`
  : audio.originalName || audio.filename;

const TourLanguageDialog: React.FC<Props> = ({ tour, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState(tour.description || '');
  const [stops, setStops] = useState<TourStop[]>([]);
  const [audioFiles, setAudioFiles] = useState<AudioFile[]>([]);
  const [mapping, setMapping] = useState<Record<string, string | null>>({});
  const [stopTitles, setStopTitles] = useState<Record<string, string>>({});
  const [musicMapping, setMusicMapping] = useState<Record<string, string | null>>({});
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingStop, setUploadingStop] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [loadAttempt, setLoadAttempt] = useState(0);
  const busy = useRef(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoaded(false);
    setError('');
    Promise.all([
      api.get(endpoints.tourStops(tour.id)),
      api.get(endpoints.audioFiles),
    ]).then(([stopsResponse, audioResponse]) => {
      if (!active) return;
      setStops([...stopsResponse.data.data].sort((a, b) => a.order - b.order));
      setAudioFiles(audioResponse.data.data);
      setLoaded(true);
    }).catch(() => {
      if (active) setError('Impossibile caricare le tappe e gli audio. Riprova.');
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [tour.id, loadAttempt]);

  const findAudio = (id?: string | null) => id
    ? audioFiles.find(file => file.id === id || file.filename === id)
    : undefined;
  const currentAudioLabel = (id?: string | null) => {
    if (!id) return 'Nessun audio';
    const file = findAudio(id);
    return file ? audioLabel(file) : `Audio non presente in libreria (${id})`;
  };
  const missingAudio = stops.filter(stop => stop.audioFileId && !mapping[stop.id]).length;
  const sameName = name.trim().toLocaleLowerCase() === tour.name.trim().toLocaleLowerCase();
  const disabled = saving || uploadingStop !== null;

  const uploadAudio = async (stop: TourStop, file: File) => {
    if (busy.current) return;
    busy.current = true;
    setUploadingStop(stop.id);
    setError('');
    try {
      const data = new FormData();
      data.append('file', file);
      data.append('title', file.name.replace(/\.[^/.]+$/, ''));
      const response = await api.post(`${endpoints.audioFiles}/upload`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const audio: AudioFile = response.data.data.audioFile;
      setAudioFiles(current => [audio, ...current.filter(item => item.id !== audio.id)]);
      setMapping(current => ({ ...current, [stop.id]: audio.id }));
    } catch (err: any) {
      setError(err.response?.data?.message || `Impossibile caricare ${file.name}. Riprova.`);
    } finally {
      busy.current = false;
      setUploadingStop(null);
    }
  };

  const save = async () => {
    if (busy.current || !loaded || !name.trim() || sameName || missingAudio) return;
    busy.current = true;
    setSaving(true);
    setError('');
    try {
      const response = await api.post(endpoints.tourTranslations(tour.id), {
        name: name.trim(),
        description: description.trim(),
        stops: stops.map(stop => ({
          sourceStopId: stop.id,
          audioFileId: mapping[stop.id] || null,
          ...(Object.prototype.hasOwnProperty.call(stopTitles, stop.id)
            ? { title: stopTitles[stop.id].trim() } : {}),
          ...(Object.prototype.hasOwnProperty.call(musicMapping, stop.id)
            ? { arBackgroundMusicId: musicMapping[stop.id] } : {}),
        })),
      });
      onCreated(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Impossibile creare il nuovo tour. Riprova.');
    } finally {
      busy.current = false;
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={() => { if (!busy.current) onClose(); }} maxWidth="md" fullWidth
      aria-labelledby="tour-language-title">
      <DialogTitle id="tour-language-title">Crea tour in un’altra lingua</DialogTitle>
      <DialogContent dividers>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Crea una versione di “{tour.name}” con un nuovo nome e nuovi audio.
          Le tappe, i beacon, i tag NFC e le altre impostazioni verranno copiati.
        </Typography>
        <TextField
          autoFocus fullWidth required label="Nome del nuovo tour" placeholder="Es. Amelia Inglese"
          value={name} onChange={event => setName(event.target.value)} disabled={disabled}
          inputProps={{ maxLength: 200 }} error={sameName}
          helperText={sameName ? 'Scegli un nome diverso dal tour di origine.' : ' '}
        />
        <TextField
          fullWidth multiline minRows={2} label="Descrizione del nuovo tour"
          value={description} onChange={event => setDescription(event.target.value)} disabled={disabled}
          inputProps={{ maxLength: 1000 }} sx={{ mb: 3 }}
        />
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <Box display="flex" alignItems="center" justifyContent="center" gap={2} py={4}>
            <CircularProgress size={24} /><Typography>Caricamento tappe e audio...</Typography>
          </Box>
        ) : !loaded ? (
          <Button onClick={() => setLoadAttempt(current => current + 1)}>Riprova</Button>
        ) : (
          <>
            <Typography variant="h6" gutterBottom>Nomi delle tappe e nuovi audio</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Scegli dalla libreria o carica un file per ogni tappa. Gli audio caricati restano nella libreria anche se annulli.
              Puoi modificare il nome di ogni tappa qui sotto. Le descrizioni vengono copiate nel nuovo tour.
            </Typography>
            {stops.length === 0 && <Alert severity="info">Questo tour non contiene tappe.</Alert>}
            {audioFiles.length === 0 && stops.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>La libreria audio è vuota. Carica i nuovi file dalle tappe qui sotto.</Alert>
            )}
            {stops.map(stop => {
              const selectedAudio = findAudio(mapping[stop.id]);
              return (
                <Paper key={stop.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <TextField
                    fullWidth label={`Nome tappa ${stop.order}`} disabled={disabled}
                    value={stopTitles[stop.id] ?? (stop.title || stop.deviceName)}
                    onChange={event => setStopTitles(current => ({ ...current, [stop.id]: event.target.value }))}
                    inputProps={{ maxLength: 200 }} sx={{ mb: 1.5 }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                    Beacon: {stop.deviceName} · {stop.deviceMacAddress}
                    {stop.nfcTagId1 && ` · NFC 1: ${stop.nfcTagId1}`}
                    {stop.nfcTagId2 && ` · NFC 2: ${stop.nfcTagId2}`}
                  </Typography>
                  <Typography variant="body2" sx={{ my: 1, overflowWrap: 'anywhere' }}>
                    Audio attuale: {currentAudioLabel(stop.audioFileId)}
                  </Typography>
                  <Box display="flex" gap={1} alignItems="flex-start" flexDirection={{ xs: 'column', sm: 'row' }}>
                    <Autocomplete
                      fullWidth disabled={disabled}
                      options={audioFiles.filter(file => file.id !== stop.audioFileId && file.filename !== stop.audioFileId)}
                      value={selectedAudio || null} getOptionLabel={audioLabel}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      onChange={(_, value) => setMapping(current => ({ ...current, [stop.id]: value?.id || null }))}
                      noOptionsText="Nessun audio disponibile: carica un nuovo file"
                      renderOption={(props, file) => <li {...props} key={file.id}>{audioLabel(file)}</li>}
                      renderInput={params => <TextField {...params}
                        label={`Nuovo audio · tappa ${stop.order}`} required={!!stop.audioFileId}
                        helperText={stop.audioFileId ? 'Seleziona un audio nella nuova lingua.' : 'Facoltativo: la tappa originale non ha audio.'}
                      />}
                    />
                    <Button component="label" variant="outlined" startIcon={<UploadIcon />} disabled={disabled}
                      sx={{ whiteSpace: 'nowrap', minHeight: 56, flexShrink: 0 }}>
                      {uploadingStop === stop.id ? 'Caricamento...' : 'Carica audio'}
                      <input hidden type="file" accept=".mp3,.wav,.aac,.ogg" disabled={disabled}
                        aria-label={`Carica audio per la tappa ${stop.order}`}
                        onChange={event => {
                          const file = event.target.files?.[0];
                          event.target.value = '';
                          if (file) uploadAudio(stop, file);
                        }} />
                    </Button>
                  </Box>
                  {uploadingStop === stop.id && <LinearProgress sx={{ mt: 1 }} />}
                  {selectedAudio?.url && <Box component="audio" controls preload="none" src={selectedAudio.url}
                    aria-label={`Ascolta il nuovo audio della tappa ${stop.order}`} sx={{ mt: 1, width: '100%' }} />}
                  {stop.arBackgroundMusicId && (
                    <Autocomplete
                      sx={{ mt: 2 }} disabled={disabled} options={audioFiles}
                      value={findAudio(musicMapping[stop.id] === undefined ? stop.arBackgroundMusicId : musicMapping[stop.id]) || null}
                      getOptionLabel={audioLabel} isOptionEqualToValue={(option, value) => option.id === value.id}
                      onChange={(_, value) => setMusicMapping(current => ({ ...current, [stop.id]: value?.id || null }))}
                      renderOption={(props, file) => <li {...props} key={file.id}>{audioLabel(file)}</li>}
                      renderInput={params => <TextField {...params} label={`Musica AR · tappa ${stop.order}`}
                        helperText={`Facoltativa. Se non la cambi, viene copiata: ${currentAudioLabel(stop.arBackgroundMusicId)}`} />}
                    />
                  )}
                </Paper>
              );
            })}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
        {loaded && <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
          {missingAudio > 0 ? `Audio da associare: ${missingAudio}` : 'Tutti gli audio richiesti sono associati'}
        </Typography>}
        <Button onClick={onClose} disabled={disabled}>Annulla</Button>
        <Button variant="contained" onClick={save}
          disabled={disabled || !loaded || !name.trim() || sameName || missingAudio > 0}>
          {saving ? 'Creazione...' : 'Crea nuovo tour'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TourLanguageDialog;
